import { IconBadgeTone, ITorqIconBadgeTone } from './torqTypes';

// Escala de radius do Design System TORQ (guia seção 29).
export const radii = {
  xs: 6,
  sm: 8,
  md: 10,
  lg: 12,
  xl: 16,
  pill: 999,
};

// Cores de marca TORQ (guia seções 4, 10, 36-39). Declarado antes de
// `darkColors`/`lightColors` porque os dois referenciam `brand.primary*`
// (foco, hover, seleção) — um valor só, trocado num lugar só quando a
// referência de azul mudar de novo (já foi #2563EB → #00E2F4 → #20CDF7).
//
// `primary` é sempre o valor exato que veio da referência visual (guia/
// screenshot) — nunca ajustado "à mão". `primaryHover`/`primaryLight` são a
// mesma matiz (~192°) só com luminosidade diferente, pra ter um degradê que
// funciona tanto pra fundo de botão quanto pra texto colorido em fundo claro
// (o tom puro é claro demais pra virar texto legível) quanto pra tingir
// fundos bem sutis — "adequar a tonalidade pra cada aplicação", não repetir
// o mesmo hex em todo canto.
export const brand = {
  primary: '#20CDF7',
  primaryHover: '#0EA5C9',
  primaryLight: '#A9EBFB',
  menuBase: '#002233',
  cyan: '#22D3EE',
  success: '#10B981',
  successButton: '#059669',
  warning: '#F59E0B',
  warningButton: '#D97706',
  error: '#F43F5E',
  errorButton: '#E11D48',
  purple: '#8B5CF6',
};

// Cores primitivas — tema escuro (guia seções 5-8, 11).
export const darkColors = {
  page: '#07101D',
  sidebar: '#06101C',
  level2: '#0B1726',
  card: '#101D2D',
  elevated: '#132236',

  borderDefault: 'rgba(148, 163, 184, 0.10)',
  borderStrong: 'rgba(148, 163, 184, 0.16)',
  borderFocus: brand.primary,

  textPrimary: '#F1F5F9',
  textSecondary: '#94A3B8',
  textMuted: '#64748B',
  textDisabled: 'rgba(148, 163, 184, 0.45)',

  hover: 'rgba(255, 255, 255, 0.035)',
  selected: 'rgba(32, 205, 247, 0.14)',

  scrollbarThumb: 'rgba(148, 163, 184, 0.22)',
  scrollbarThumbHover: 'rgba(148, 163, 184, 0.32)',

  dialogShadow: '0 8px 30px rgba(0, 0, 0, 0.18)',
};

// Cores primitivas — tema claro (guia seção 9).
export const lightColors = {
  // Fundo da página bem levemente tingido de #002233 (a cor da sidebar) em
  // vez de um branco/cinza neutro — é o que dá a sensação de sistema
  // homogêneo entre a sidebar escura e o conteúdo claro (guia seção 1,
  // "Fundo da página"), tanto no menu escuro quanto no menu claro (os dois
  // usam essas mesmas `lightColors`). Cards/superfícies continuam em
  // branco puro — só a página em volta é que ganha a leve quebra de branco.
  page: '#F5F7FA',
  paper: '#FFFFFF',
  level2: '#F8FAFC',
  card: '#FFFFFF',
  elevated: '#FFFFFF',

  borderDefault: '#E5EAF1',
  borderStrong: '#DDE4EC',
  borderFocus: brand.primary,
  divider: '#E8EDF3',

  textPrimary: '#172033',
  textSecondary: '#64748B',
  textMuted: '#94A3B8',
  textDisabled: 'rgba(100, 116, 139, 0.45)',

  hover: 'rgba(32, 205, 247, 0.06)',
  selected: '#E3F9FE',

  scrollbarThumb: 'rgba(100, 116, 139, 0.20)',
  scrollbarThumbHover: 'rgba(100, 116, 139, 0.30)',

  dialogShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
};

// Gradientes do IconBadge por tom (guia seção 25) — mesmos valores nos dois
// temas, já que são sobreposições translúcidas pensadas para funcionar tanto
// sobre um card escuro quanto sobre um card claro.
export const iconBadgeTones: Record<IconBadgeTone, ITorqIconBadgeTone> = {
  primary: {
    gradient: 'linear-gradient(145deg, rgba(37,99,235,.32), rgba(37,99,235,.08))',
    border: 'rgba(59,130,246,.25)',
    iconColor: '#60A5FA',
  },
  cyan: {
    gradient: 'linear-gradient(145deg, rgba(34,211,238,.28), rgba(8,145,178,.08))',
    border: 'rgba(34,211,238,.30)',
    iconColor: '#22D3EE',
  },
  success: {
    gradient: 'linear-gradient(145deg, rgba(16,185,129,.28), rgba(5,150,105,.08))',
    border: 'rgba(16,185,129,.30)',
    iconColor: '#34D399',
  },
  error: {
    gradient: 'linear-gradient(145deg, rgba(244,63,94,.28), rgba(190,24,93,.08))',
    border: 'rgba(244,63,94,.30)',
    iconColor: '#FB7185',
  },
  warning: {
    gradient: 'linear-gradient(145deg, rgba(245,158,11,.28), rgba(217,119,6,.08))',
    border: 'rgba(245,158,11,.30)',
    iconColor: '#FBBF24',
  },
  purple: {
    gradient: 'linear-gradient(145deg, rgba(139,92,246,.28), rgba(109,40,217,.08))',
    border: 'rgba(139,92,246,.30)',
    iconColor: '#A78BFA',
  },
  neutral: {
    gradient: 'linear-gradient(145deg, rgba(148,163,184,.24), rgba(100,116,139,.08))',
    border: 'rgba(148,163,184,.25)',
    iconColor: '#94A3B8',
  },
};

// Escala tipográfica nomeada (guia seção 12) — consumida tanto pelo
// `typography` do MUI quanto por componentes que precisem dos mesmos números
// fora de uma variante MUI (ex.: valor de um stat card).
export const typographyScale = {
  pageTitle: { fontSize: '1.375rem', fontWeight: 650 },
  sectionTitle: { fontSize: '1rem', fontWeight: 600 },
  cardTitle: { fontSize: '0.75rem', fontWeight: 500 },
  cardValue: { fontSize: '1.125rem', fontWeight: 650 },
  body: { fontSize: '0.875rem', fontWeight: 400 },
  secondary: { fontSize: '0.8125rem', fontWeight: 400 },
  caption: { fontSize: '0.75rem', fontWeight: 400 },
  button: { fontSize: '0.8125rem', fontWeight: 600 },
  tableHeader: { fontSize: '0.75rem', fontWeight: 600 },
  tableBody: { fontSize: '0.8125rem', fontWeight: 400 },
};

// Array de 25 sombras exigido pelo MUI (Theme['shadows']) — só as primeiras
// entradas são diferenciadas (uso real: dialogs/menus/popovers, guia seção
// 31); o restante repete a sombra "forte" do tema por consistência, em vez
// dos placeholders idênticos que existiam antes.
export const buildShadows = (mode: 'light' | 'dark'): import('@mui/material/styles').Theme['shadows'] => {
  const soft = mode === 'dark' ? 'rgba(0,0,0,0.18)' : 'rgba(15,23,42,0.05)';
  const strong = mode === 'dark' ? darkColors.dialogShadow : lightColors.dialogShadow;

  const shadows = [
    'none',
    `0 1px 3px ${soft}`,
    `0 2px 6px ${soft}`,
    `0 4px 12px ${soft}`,
    `0 6px 18px ${soft}`,
  ];
  while (shadows.length < 25) shadows.push(strong);

  return shadows as unknown as import('@mui/material/styles').Theme['shadows'];
};
